import { motion } from "framer-motion";

export default function PrivacyPage() {
  return (
    <main data-testid="privacy-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-16 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4"
          >
            Legal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl text-white mb-4"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-500 text-sm"
          >
            Last updated: March 2025
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto prose prose-invert prose-slate">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-white text-2xl mb-4">Introduction</h2>
              <p className="text-slate-400 leading-relaxed">
                Unleashed Ventures ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website. Please read this Privacy Policy carefully. By using the Site, you consent to the data practices described in this policy.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">1. Information We Collect</h2>
              
              <h3 className="text-white text-lg mt-6 mb-3">Personal Information</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li>Fill out our contact form</li>
                <li>Subscribe to our newsletter</li>
                <li>Request information about our services</li>
                <li>Communicate with us via email or other channels</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                This information may include your name, email address, phone number, company name, and any other information you choose to provide.
              </p>

              <h3 className="text-white text-lg mt-6 mb-3">Automatically Collected Information</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                When you visit our Site, we may automatically collect certain information about your device and usage, including:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li>IP address and geographic location</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Date and time of visits</li>
              </ul>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                We may use the information we collect for various purposes, including to:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li>Respond to your inquiries and fulfill your requests</li>
                <li>Send you newsletters and marketing communications (with your consent)</li>
                <li>Improve our website and services</li>
                <li>Analyze usage patterns and trends</li>
                <li>Protect against fraudulent or illegal activity</li>
                <li>Comply with legal obligations</li>
                <li>Communicate with you about updates to our policies</li>
              </ul>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">3. Disclosure of Your Information</h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                We may share your information in the following situations:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li><strong className="text-white">Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf, such as email delivery, hosting, and analytics.</li>
                <li><strong className="text-white">Legal Requirements:</strong> We may disclose information if required by law or in response to valid requests by public authorities.</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets, your information may be transferred.</li>
                <li><strong className="text-white">Protection of Rights:</strong> We may disclose information to protect and defend our rights and property.</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties for their marketing purposes.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our Site and store certain information. Cookies are files with a small amount of data that are sent to your browser from a website and stored on your device.
              </p>
              <p className="text-slate-400 leading-relaxed mb-4">
                We use the following types of cookies:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li><strong className="text-white">Essential Cookies:</strong> Required for the operation of our Site</li>
                <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors interact with our Site</li>
                <li><strong className="text-white">Functionality Cookies:</strong> Enable enhanced functionality and personalization</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                You can instruct your browser to refuse all cookies or indicate when a cookie is being sent. However, if you do not accept cookies, some features of our Site may not function properly.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">5. Data Security</h2>
              <p className="text-slate-400 leading-relaxed">
                We implement appropriate technical and organizational security measures designed to protect the security of your personal information. However, please note that no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">6. Data Retention</h2>
              <p className="text-slate-400 leading-relaxed">
                We will retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">7. Your Privacy Rights</h2>
              <p className="text-slate-400 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="text-slate-400 space-y-2 list-disc list-inside">
                <li><strong className="text-white">Access:</strong> Request access to your personal information</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information</li>
                <li><strong className="text-white">Opt-out:</strong> Opt out of marketing communications</li>
                <li><strong className="text-white">Data Portability:</strong> Request a copy of your data in a portable format</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">8. Third-Party Links</h2>
              <p className="text-slate-400 leading-relaxed">
                Our Site may contain links to third-party websites. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to read the privacy policy of every website you visit.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">9. Children's Privacy</h2>
              <p className="text-slate-400 leading-relaxed">
                Our Site is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">10. International Data Transfers</h2>
              <p className="text-slate-400 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. These countries may have different data protection laws. By using our Site, you consent to the transfer of your information to these countries.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-slate-400 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div>
              <h2 className="text-white text-2xl mb-4">12. Contact Us</h2>
              <p className="text-slate-400 leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-6 bg-slate-900/50 border border-white/5">
                <p className="text-white font-medium">Unleashed Ventures</p>
                <p className="text-slate-400 mt-2">Email: privacy@unleashedventures.com</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
